import { IViewConfigSchema } from "../../../../vendor_core/core/src/baseConfigs/layoutConfigShema";
import Locale from "../../../../vendor_core/core/src/utils/Locale";
import Configs from "../../../../vendor_core/src/vendorConfigs/Configs";
import { IRulesParagraph } from "./BURRulesScrollView";

/** An icon of a PSD page: the texture id in the atlas and the size it has in the layout (PSD pixels) */
export interface IPageIcon {
    textureId: string;
    width: number;
    height: number;
}

/** A symbol or feature with a short description (paytable pages with the special symbols and the energy coins) */
export interface IFeatureItem {
    icon?: IPageIcon;
    title: string;
    description: string;
}

/** A symbol with the payouts for the cluster sizes, from the biggest to the smallest */
export interface IPayoutSymbol {
    icon?: IPageIcon;
    rows: { range: string; payoutKey: string }[];
}

interface IPageBase {
    /** Scene name, e.g. "MenusContent-paytable~3" */
    id: string;
    title: string;
}

export interface IFeaturesPage extends IPageBase {
    kind: "features";
    items: IFeatureItem[];
}

export interface IPayoutsPage extends IPageBase {
    kind: "payouts";
    symbols: IPayoutSymbol[];
}

export interface IRulesPage extends IPageBase {
    kind: "rules";
    paragraphs: IRulesParagraph[];
    /** Label of the button that opens the history, on the page that has one */
    historyLabel?: string;
}

export type ISettingsPage = IFeaturesPage | IPayoutsPage | IRulesPage;

const SELECTED_SPRITE = "selected_info";
const RULES_BULLET = "● ";
/** The page header texts of the layouts (the id of the PSD text field, which differs from its localization key) */
const PAGE_HEADER = /^(paytable|ui_game_rules|ui_feature_rules|ui_history|ui_cluster_pays)$/;
const FEATURE_DESCRIPTION = /^ui_(.+)_description$/;
const PAYOUT_PRIZE = /^pt_sym_/;
const FEATURE_TITLE = /^ui_feature_(\d+)$/;
const BULLET_TEXT = /^(?:feature_\d+_text|cluster_pays)_(\d+)$/;
const RULES_BODY = /^rules_pg_\d+$/;
/** The single game rules page: a title per section ("ui_game_rule_3") and its texts ("game_rule_3_text_1") */
const GAME_RULE_TITLE = /^ui_game_rule_(\d+)$/;
const GAME_RULE_TEXT = /^game_rule_(\d+)_text_(\d+)$/;

interface IPsdText {
    id: string;
    content: string;
}

interface IPsdSprite {
    id: string;
    width: number;
    height: number;
}

/** The texts and sprites of a scene, from the scene itself and from its groups, in the order of the layout */
function collect(config: IViewConfigSchema): { texts: IPsdText[]; sprites: IPsdSprite[] } {
    const texts: IPsdText[] = [];
    const sprites: IPsdSprite[] = [];
    [config, ...Object.values(config.groups || {})].forEach((source: any) => {
        (source.text || []).forEach((text: any) => {
            texts.push({ id: String(text.id).split("~")[0], content: text.content });
        });
        (source.sprites || []).forEach((sprite: any) => {
            if (sprite.id !== SELECTED_SPRITE) {
                sprites.push({ id: sprite.id, width: sprite.width, height: sprite.height });
            }
        });
    });
    return { texts, sprites };
}

function localized(key: string): string {
    return Locale.getString(key);
}

/**
 * Reads what a paytable / rules page shows from the layout of its PSD scene: its texts (the localization keys), its
 * images and which belongs to what. Where things are placed in the layout is not used: the panel spaces them evenly.
 */
export function parsePage(sceneName: string, config: IViewConfigSchema): ISettingsPage {
    const { texts, sprites } = collect(config);
    const header = texts.find((text) => PAGE_HEADER.test(text.id));
    const title = header ? localized(header.content) : "";
    const icon = (spriteId: string): IPageIcon | undefined => {
        const sprite = sprites.find((candidate) => candidate.id === spriteId);
        return sprite && { textureId: `${sceneName}~%/${sprite.id}`, width: sprite.width, height: sprite.height };
    };

    if (texts.some((text) => PAYOUT_PRIZE.test(text.id))) {
        const symbols: IPayoutSymbol[] = sprites
            .filter((sprite) => sprite.id.startsWith("sym_"))
            .map((sprite) => {
                const symbol = sprite.id.substring("sym_".length);
                const rows = texts
                    .map((text) => text.id.match(new RegExp(`^${symbol}_(\\d+)$`)))
                    .filter((match): match is RegExpMatchArray => !!match)
                    .map((match) => Number(match[1]))
                    .sort((a, b) => b - a)
                    .map((size) => ({
                        range: localized(texts.find((text) => text.id === `${symbol}_${size}`)!.content),
                        payoutKey: `pt_sym_${symbol}_${size}`
                    }));
                return { icon: icon(sprite.id), rows };
            });
        return { kind: "payouts", id: sceneName, title, symbols };
    }

    const descriptions = texts.filter((text) => FEATURE_DESCRIPTION.test(text.id));
    if (descriptions.length > 0) {
        const items: IFeatureItem[] = descriptions.map((description) => {
            const name = description.id.match(FEATURE_DESCRIPTION)![1];
            const itemTitle = texts.find((text) => text.id === `ui_${name}`);
            return {
                icon: icon(`sym_${name}`),
                title: itemTitle ? localized(itemTitle.content) : "",
                description: localized(description.content)
            };
        });
        // the items follow the order of the images in the layout
        items.sort((a, b) => spriteOrder(sprites, a.icon) - spriteOrder(sprites, b.icon));
        return { kind: "features", id: sceneName, title, items };
    }

    const paragraphs: IRulesParagraph[] = [];
    const featureTitle = texts.find((text) => FEATURE_TITLE.test(text.id));
    if (featureTitle) {
        paragraphs.push({ text: localized(featureTitle.content), bold: true, center: true });
    }

    texts
        .filter((text) => BULLET_TEXT.test(text.id))
        .sort((a, b) => Number(a.id.match(BULLET_TEXT)![1]) - Number(b.id.match(BULLET_TEXT)![1]))
        .forEach((text) => paragraphs.push({ text: RULES_BULLET + localized(text.content).trim() }));

    texts
        .filter((text) => GAME_RULE_TITLE.test(text.id))
        .sort((a, b) => Number(a.id.match(GAME_RULE_TITLE)![1]) - Number(b.id.match(GAME_RULE_TITLE)![1]))
        .forEach((title) => {
            const section = title.id.match(GAME_RULE_TITLE)![1];
            paragraphs.push({ text: localized(title.content), bold: true, center: true });
            texts
                .filter((text) => text.id.match(GAME_RULE_TEXT)?.[1] === section)
                .sort((a, b) => Number(a.id.match(GAME_RULE_TEXT)![2]) - Number(b.id.match(GAME_RULE_TEXT)![2]))
                .forEach((text) => paragraphs.push({ text: RULES_BULLET + localized(text.content).trim() }));
        });

    texts
        .filter((text) => RULES_BODY.test(text.id))
        .forEach((text) => paragraphs.push(...gameRulesParagraphs(text.id)));

    const historyLabel = texts.find((text) => text.id.startsWith("label-"));
    return {
        kind: "rules",
        id: sceneName,
        title,
        paragraphs,
        historyLabel: historyLabel && localized(historyLabel.content)
    };
}

function spriteOrder(sprites: IPsdSprite[], icon?: IPageIcon): number {
    const index = icon ? sprites.findIndex((sprite) => icon.textureId.endsWith(`/${sprite.id}`)) : -1;
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/** The body of a game rules page is made of the localization keys of the settings config, one text per paragraph */
function gameRulesParagraphs(fieldId: string): IRulesParagraph[] {
    const rule = (Configs.settings?.rulesData || []).find((data) => data.sceneTextField === fieldId);
    if (!rule) {
        return [];
    }

    const [first, last] = rule.rulesDimensions;
    const paragraphs: IRulesParagraph[] = [];
    for (let index = first; index <= last; index++) {
        paragraphs.push({ text: RULES_BULLET + localized(`${rule.name}${index}`).trim() });
    }
    return paragraphs;
}
